import { Gauge, AlertTriangle, CheckCircle2, MousePointerClick } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { COLOR } from '@/constants/testIds';
import { scoreTone } from '@/components/color/colorUtils';

export const ConsistencyPanel = ({ consistency, onSelectIssue }) => {
  const c = consistency;
  const score = c?.score;
  const r = 26;
  const circ = 2 * Math.PI * r;
  return (
    <section data-testid={COLOR.consistencyPanel} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))]"><Gauge className="h-4 w-4 text-[hsl(var(--lxra-gold))]" /> Gallery colour consistency</p>
      <div className="mt-3 flex flex-wrap items-center gap-5">
        <div className="relative h-16 w-16 shrink-0">
          <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
            <circle cx="32" cy="32" r={r} fill="none" stroke="#1F1F22" strokeWidth="5" />
            {score != null && <circle cx="32" cy="32" r={r} fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeDasharray={`${(score / 100) * circ} ${circ}`} className={`${scoreTone(score)} transition-[stroke-dasharray] duration-500`} />}
          </svg>
          <span data-testid={COLOR.consistencyScore} className={`absolute inset-0 flex items-center justify-center font-mono text-sm font-semibold ${scoreTone(score)}`}>{score == null ? '—' : `${score}%`}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p data-testid={COLOR.consistencyMessage} className="text-sm text-[hsl(var(--lxra-fg))]">{c?.message || 'Run an analysis to score how cohesive this gallery reads.'}</p>
          {c?.target_source && <p className="mt-0.5 text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Target: {c.target_source} · {c.analyzed} analysed</p>}
        </div>
      </div>
      {c?.issues?.length > 0 && (
        <ul className="mt-4 space-y-1.5">
          {c.issues.map((iss, i) => (
            <li key={`${iss.metric}-${iss.direction}`} data-testid={COLOR.consistencyIssue(i)} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] px-3 py-2">
              <div className="flex min-w-0 items-start gap-2">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" />
                <div className="min-w-0">
                  <p className="text-xs text-[hsl(var(--lxra-fg))]">{iss.message}</p>
                  <p className="text-[11px] text-[hsl(var(--lxra-gold))]">{iss.fix}</p>
                </div>
              </div>
              <Button size="sm" variant="outline" data-testid={COLOR.consistencyIssueSelect(i)} onClick={() => onSelectIssue(iss)} className="h-7 border-[hsl(var(--lxra-border))] bg-transparent text-[10px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))]"><MousePointerClick className="mr-1 h-3 w-3" /> Select {iss.count}</Button>
            </li>
          ))}
        </ul>
      )}
      {c && c.score != null && c.issues?.length === 0 && <p className="mt-3 flex items-center gap-1.5 text-xs text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" /> No clusters drifting from the target.</p>}
    </section>
  );
};
